import { DialogConfig } from '@angular/cdk/dialog';
import { DestroyRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DialogRef, dialogSize, overlayPosition, SbDialogService } from '@sb/ui';

interface DialogContext<T = unknown, D = unknown, R = unknown> {
  size: dialogSize;
  position?: overlayPosition;
  config?: DialogConfig<D, DialogRef<R, T>>;
}
@Directive({
  standalone: true,
  selector: '[appRouteDialog]',
})
export class RouteDialogDirective<T = unknown, D = unknown, R = unknown> implements OnInit, OnDestroy {
  private readonly defaultSize: dialogSize = 'md';
  private readonly defaultOverlayPosition: overlayPosition = 'center';
  private dialogSize: dialogSize = this.defaultSize;
  private overlayPosition: overlayPosition = this.defaultOverlayPosition;
  private dialogConfig?: DialogConfig<D, DialogRef<R, T>>;
  private dialogRef: DialogRef<R, T> | undefined;

  public constructor(
    private readonly templateRef: TemplateRef<any>,
    private readonly router: Router,
    private readonly dialogService: SbDialogService,
    private readonly destroyRef: DestroyRef,
  ) {}

  @Input()
  public set appRouteDialog(context: DialogContext<T, D, R>) {
    // if we need to update the size on the fly we can do it here
    this.dialogSize = context.size || this.defaultSize;
    this.overlayPosition = context.position || this.defaultOverlayPosition;
    this.dialogConfig = context.config;

    // Update size if dialog is opened
    if (this.dialogRef) {
      this.dialogService.updateSize(this.dialogRef, this.dialogSize);
    }
  }

  @Output()
  public closed = new EventEmitter<unknown>();

  public ngOnInit(): void {
    this.open();
    this.watchForClose();
  }

  public ngOnDestroy(): void {
    this.close();
  }

  public close(result?: R): void {
    this.dialogRef?.close(result);
  }

  private open(): void {
    this.dialogRef = this.dialogService
      .setSize(this.dialogSize)
      .setPosition(this.overlayPosition)
      .open<T, D, R>(this.templateRef, this.dialogConfig);
  }

  private watchForClose(): void {
    void this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.closed.emit(result);
        this.router.navigate(['', { outlets: { modal: null } }]);
      },
    });
  }
}
